import axios from 'axios';
import moment from 'moment';
import tzLookup from 'tz-lookup';
import { Vue } from 'vue-property-decorator';
import {
  APIGetRequest,
  WebviewQueryStringParameters,
  EntranceEntity,
  LowestUtilizedEntranceReduceReturn,
  LocationEntity,
  APIPostRequest,
  APIDeleteRequest,
} from '../../types';

const apiKey = process.env.VUE_APP_API_KEY || '';

export const logError = async (err: any) => {
  await (Vue as any).rollbar.error(err);
};

export const logDebug = (message: any) => {
  (Vue as any).rollbar.debug(message);
};

export const rangeArray = (start: number, end: number): number[] => {
  return Array.from({ length: end - start + 1 }, (v, k) => k + start);
};

export const getLowestUtilizedEntrance = (entrances: EntranceEntity[]): EntranceEntity | null => {
  if (entrances.length === 1) {
    return entrances[0];
  } else if (entrances.length === 0) {
    return null;
  }

  const lowestUtilizedEntrance = entrances.reduce(
    (accum, entrance): LowestUtilizedEntranceReduceReturn => {
      const reservationDateForEntrance = entrance.reservationDates!.find((reservationDateItem) =>
        moment().isSame(moment(reservationDateItem.date), 'day'),
      );

      if (accum.entrance === null) {
        accum.entrance = entrance;
        accum.numberOfOccupants = reservationDateForEntrance ? reservationDateForEntrance.numberOfOccupants : 0;
      } else {
        if (reservationDateForEntrance && reservationDateForEntrance.numberOfOccupants < accum.numberOfOccupants) {
          accum.entrance = entrance;
          accum.numberOfOccupants = reservationDateForEntrance.numberOfOccupants;
        } else if (!reservationDateForEntrance) {
          accum.entrance = entrance;
          accum.numberOfOccupants = 0;
        }
      }

      return accum;
    },
    {
      numberOfOccupants: 0,
      entrance: null,
    } as LowestUtilizedEntranceReduceReturn,
  );

  if (lowestUtilizedEntrance.entrance !== null) {
    return lowestUtilizedEntrance.entrance;
  } else {
    return entrances.reduce((accum, entrance): EntranceEntity => {
      if (
        accum === null ||
        (!accum.maxOccupancy && entrance.maxOccupancy) ||
        (accum.maxOccupancy && entrance.maxOccupancy && entrance.maxOccupancy > accum.maxOccupancy)
      ) {
        accum = entrance;
      }

      return accum;
    }, null as EntranceEntity | null);
  }
};

export const getLocationTimezoneName = (location: LocationEntity): string => {
  const timezoneName = tzLookup(location.latitude, location.longitude);

  return timezoneName;
};

export const validateHexColor = (rawHexColor: string): boolean => {
  return /^([0-9A-F]{3}){1,2}$/i.test(rawHexColor);
};

export const validateQueryParameters = (queryParameters: any) => {
  // if (!queryParameters.hasOwnProperty('apiKey')) {
  //   throw new Error('The `apiKey` or `qrCode` query parameter must be provided');
  // }

  // if (!queryParameters.hasOwnProperty('customerId')) {
  //   throw new Error('The `customerId` query parameter must be provided');
  // }

  // if (!queryParameters.hasOwnProperty('occupantTypeId')) {
  //   throw new Error('The `occupantTypeId` query parameter must be provided');
  // }

  if (
    queryParameters.hasOwnProperty('widgetLayout') &&
    ![null, '1-col', '1-col-short', '1-col-condensed', '2-col', '3-col'].includes(queryParameters.widgetLayout)
  ) {
    throw new Error('The `widgetLayout` query parameter is not set to a valid layout option');
  }
};

export const parseQueryParameters = (rawQueryParameters: any): WebviewQueryStringParameters => {
  validateQueryParameters(rawQueryParameters);

  const queryParameters: WebviewQueryStringParameters = {
    apiKey: rawQueryParameters.apiKey,
    customerId: parseInt(rawQueryParameters.customerId, 10),
    externalUserId: rawQueryParameters.externalUserId || rawQueryParameters.userSub || null,
    webview: rawQueryParameters.webview === 'true',
    occupantTypeId: parseInt(rawQueryParameters.occupantTypeId, 10),
    visitTypeId: rawQueryParameters.visitTypeId ? parseInt(rawQueryParameters.visitTypeId, 10) : null,
    fcmToken: rawQueryParameters.fcmToken || null,
    name: rawQueryParameters.name && rawQueryParameters.name.length ? rawQueryParameters.name : null,
    email: rawQueryParameters.email && rawQueryParameters.email.length ? rawQueryParameters.email : null,
    locationId: rawQueryParameters.locationId ? parseInt(rawQueryParameters.locationId, 10) : null,
    userLatitude: rawQueryParameters.userLatitude ? parseFloat(rawQueryParameters.userLatitude) : null,
    userLongitude: rawQueryParameters.userLongitude ? parseFloat(rawQueryParameters.userLongitude) : null,
    lang: rawQueryParameters.lang || 'en',
    widgetLayout: rawQueryParameters.widgetLayout || null,
    qrCode: rawQueryParameters.qrCode || null,
    cssURL: rawQueryParameters.cssURL || null,
    primaryColor:
      rawQueryParameters.primaryColor && validateHexColor(rawQueryParameters.primaryColor) ? `#${rawQueryParameters.primaryColor}` : '#00B5EF',
    primaryFontColor:
      rawQueryParameters.primaryFontColor && validateHexColor(rawQueryParameters.primaryFontColor)
        ? `#${rawQueryParameters.primaryFontColor}`
        : '#00B5EF',
    primaryButtonFontColor:
      rawQueryParameters.primaryButtonFontColor && validateHexColor(rawQueryParameters.primaryButtonFontColor)
        ? `#${rawQueryParameters.primaryButtonFontColor}`
        : '#FFF',
    secondColor:
      rawQueryParameters.secondColor && validateHexColor(rawQueryParameters.secondColor) ? `#${rawQueryParameters.secondColor}` : '#5CD895',
    secondFontColor:
      rawQueryParameters.secondFontColor && validateHexColor(rawQueryParameters.secondFontColor)
        ? `#${rawQueryParameters.secondFontColor}`
        : '#5CD895',
    secondButtonFontColor:
      rawQueryParameters.secondButtonFontColor && validateHexColor(rawQueryParameters.secondButtonFontColor)
        ? `#${rawQueryParameters.secondButtonFontColor}`
        : '#FFF',
    thirdColor: rawQueryParameters.thirdColor && validateHexColor(rawQueryParameters.thirdColor) ? `#${rawQueryParameters.thirdColor}` : '#7229B2',
    thirdFontColor:
      rawQueryParameters.thirdFontColor && validateHexColor(rawQueryParameters.thirdFontColor) ? `#${rawQueryParameters.thirdFontColor}` : '#7229B2',
    thirdButtonFontColor:
      rawQueryParameters.thirdButtonFontColor && validateHexColor(rawQueryParameters.thirdButtonFontColor)
        ? `#${rawQueryParameters.thirdButtonFontColor}`
        : '#FFF',
    fourthColor:
      rawQueryParameters.fourthColor && validateHexColor(rawQueryParameters.fourthColor) ? `#${rawQueryParameters.fourthColor}` : '#1E1B1D',
    fourthFontColor:
      rawQueryParameters.fourthFontColor && validateHexColor(rawQueryParameters.fourthFontColor)
        ? `#${rawQueryParameters.fourthFontColor}`
        : '#1E1B1D',
    fourthButtonFontColor:
      rawQueryParameters.fourthButtonFontColor && validateHexColor(rawQueryParameters.fourthButtonFontColor)
        ? `#${rawQueryParameters.fourthButtonFontColor}`
        : '#FFF',
    headerBackgroundColor:
      rawQueryParameters.headerBackgroundColor && validateHexColor(rawQueryParameters.headerBackgroundColor)
        ? `#${rawQueryParameters.headerBackgroundColor}`
        : '#1E1B1D',
    languagePickerColor:
      rawQueryParameters.languagePickerColor && validateHexColor(rawQueryParameters.languagePickerColor)
        ? `#${rawQueryParameters.languagePickerColor}`
        : '#FFF',
  };

  return queryParameters;
};

export const apiGetRequest = async (options: APIGetRequest): Promise<any> => {
  try {
    const headers = {
      'x-api-key': apiKey,
      'content-api-key': options.apiKey,
    };

    const apiReturn = await axios.get(options.apiURL, { headers });

    if (apiReturn.data && apiReturn.data.success && apiReturn.data.data) {
      return apiReturn.data.data;
    } else {
      return null;
    }
  } catch (e) {
    console.log(e);

    const statusCode = e && e.response && e.response.status ? e.response.status : null;

    if (statusCode === 500) {
      await logError(e);
    }

    return null;
  }
};

export const apiPostRequest = async (options: APIPostRequest): Promise<any> => {
  try {
    const headers = {
      'x-api-key': apiKey,
      'content-api-key': options.apiKey,
    };

    const apiReturn = await axios.post(options.apiURL, options.postBody, {
      headers,
    });

    return apiReturn.data;
  } catch (e) {
    console.log(e);

    if (e && e.response && e.response.status === 500) {
      await logError(e);
    }

    return e.response;
  }
};

export const apiDeleteRequest = async (options: APIDeleteRequest): Promise<boolean> => {
  try {
    const headers = {
      'x-api-key': apiKey,
      'content-api-key': options.apiKey,
    };

    const apiReturn = await axios.delete(options.apiURL, { headers });

    if (apiReturn.data && apiReturn.data.success) {
      return true;
    } else {
      return false;
    }
  } catch (e) {
    console.log(e);

    const statusCode = e && e.response && e.response.status ? e.response.status : null;

    if (statusCode === 500) {
      await logError(e);
    }

    return false;
  }
};

export const checkAllowedLocation = (userLatitude: number | null, userLongitude: number | null, location: LocationEntity): boolean => {
  const locationGeorestrictionRadius = location.georestrictionRadius;

  if (locationGeorestrictionRadius === 0) {
    return true;
  } else {
    const locationLatitude = location.latitude;
    const locationLongitude = location.longitude;

    if (!userLatitude || !userLongitude) {
      return false;
    }

    if (locationLatitude === userLatitude && locationLongitude === userLongitude) {
      return true;
    } else {
      const radlat1 = (Math.PI * locationLatitude) / 180;
      const radlat2 = (Math.PI * userLatitude) / 180;
      const theta = locationLongitude - userLongitude;
      const radtheta = (Math.PI * theta) / 180;

      let distance = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

      distance = Math.acos(distance);
      distance = (distance * 180) / Math.PI;
      distance = distance * 60 * 1.1515;

      return distance <= locationGeorestrictionRadius;
    }
  }
};
